/** Import libs */
/** -------------------------------------- */

// Alpine.js
import Alpine from 'alpinejs'
import intersect from '@alpinejs/intersect'

// Import Headroom.js
import Headroom from "headroom.js";

// Import BalanceText
import balanceText from 'balance-text';

// Import custom file upload functionality
import { initFileUploads } from './file-upload.js';

/** Functions */
/** -------------------------------------- */

// Resolve lazy chunks relative to this script, including when assets use a CDN.
__webpack_public_path__ = new URL('../../', document.currentScript.src).href;

// Optional UI styles must load before their widgets are initialized.
let componentStyles;
function loadComponentStyles() {
    if (!componentStyles) componentStyles = new Promise((resolve, reject) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = window.cashitComponentStyles;
        link.onload = resolve;
        link.onerror = () => reject(new Error('Could not load component styles.'));
        document.head.appendChild(link);
    });
    return componentStyles;
}


// Register the custom element only on pages that actually use Lottie.
if (document.querySelector('lottie-player')) {
    import(/* webpackChunkName: "lottie-player" */ '@lottiefiles/lottie-player')
        .catch(error => console.error('Could not load Lottie animations.', error));
}
 
// Alpine.js
Alpine.plugin(intersect)
window.Alpine = Alpine
 
Alpine.start()

// Load sharing tools only on pages with a copy-link button.
if (document.querySelector('.js-clipboard-link')) {
    loadComponentStyles().then(() => import(/* webpackChunkName: "share-tools" */ './components/share-tools'))
        .then(({ initShareTools }) => initShareTools())
        .catch(error => console.error('Could not load sharing tools.', error));
}

// Keep content visible until the optional animation library has initialized.
if (document.querySelector('[data-aos]')) {
    loadComponentStyles().then(() => import(/* webpackChunkName: "scroll-animations" */ 'aos'))
        .then(({ default: AOS }) => {
            AOS.init();
            document.body.classList.add('aos-enabled');
        })
        .catch(error => {
            document.body.classList.remove('aos-enabled');
            console.error('Could not load scroll animations.', error);
        });
}

// Gallery links remain normal image links if the optional lightbox fails.
if (document.querySelector('.chocolat-image')) {
    loadComponentStyles().then(() => import(/* webpackChunkName: "lightbox" */ 'chocolat'))
        .then(({ default: Chocolat }) => Chocolat(document.querySelectorAll('.chocolat-image'), { loop: true }))
        .catch(error => console.error('Could not load the gallery lightbox.', error));
}

// Cache navigation elements and only change classes when crossing the page top.
const headers = [...document.querySelectorAll('header')];
const scrolledHeaderColor = document.querySelector('.desktop-nav--white') ? 'bg-black' : 'bg-white';
let headerScrolled;
let headerUpdatePending = false;
function updateHeaderBackground() {
    headerUpdatePending = false;
    const scrolled = window.scrollY > 0;
    if (scrolled === headerScrolled) return;
    headerScrolled = scrolled;
    headers.forEach(header => {
        header.classList.toggle('bg-transparent', !scrolled);
        header.classList.toggle('bg-black', scrolled && scrolledHeaderColor === 'bg-black');
        header.classList.toggle('bg-white', scrolled && scrolledHeaderColor === 'bg-white');
    });
}
updateHeaderBackground();
window.addEventListener('scroll', () => {
    if (headerUpdatePending) return;
    headerUpdatePending = true;
    requestAnimationFrame(updateHeaderBackground);
}, { passive: true });

// Initialize only navigation elements that exist.
document.querySelectorAll('header.desktop-nav, header.mobile-nav').forEach(header => {
    new Headroom(header, { offset: 300 }).init();
});


/**
 * Line animation
 */
if (document.getElementById('line-animation')) {
    const container = document.getElementById('line-container');
    const wrapper = document.getElementById('line-wrapper');
    const line = document.getElementById('line-svg');
    const heading = document.querySelector('.line-heading');
    
    // Samla alla kort och deras centerpositioner
    const cards = [];
    for (let i = 1; i <= 4; i++) {
        const card = document.getElementById(`line-card-${i}`);
        if (card) {
            const cardImage = card.querySelector('img');
            cards.push({
                element: card,
                top: card.offsetTop,
                height: card.offsetHeight,
                imageHeight: cardImage ? cardImage.offsetHeight : 0
            });
        }
    }

    // Beräkna total höjd (heading + alla kort - halva sista bildens höjd)
    const lastCard = cards[cards.length - 1];
    const totalHeight = (lastCard.top + lastCard.height - (lastCard.imageHeight / 2)) - heading.offsetTop;

    // Sätt initial höjd på SVG container
    wrapper.style.height = totalHeight + 'px';
    line.style.height = totalHeight + 'px';

    let isScrolling = false;
    let currentHeight = 0;
    let animationFrame;

    function lerp(start, end, factor = 0.1) {
        return start + (end - start) * factor;
    }

    function updateHeight() {
        const scrollY = window.scrollY;
        const startPoint = heading.offsetTop;
        const progress = Math.max(0, Math.min(1, (scrollY - startPoint) / (totalHeight)));
        
        // Beräkna target height
        const targetHeight = progress * totalHeight;
        
        // Använd lerp för mjukare animation
        currentHeight = lerp(currentHeight, targetHeight, 0.1);
        
        // Uppdatera max-height för att visa/dölja linjen
        wrapper.style.maxHeight = currentHeight + 'px';
        
        // Fortsätt animera om vi inte har nått target
        if (Math.abs(currentHeight - targetHeight) > 0.1) {
            animationFrame = requestAnimationFrame(updateHeight);
        } else {
            isScrolling = false;
        }
    }

    function handleScroll() {
        if (!isScrolling) {
            isScrolling = true;
            cancelAnimationFrame(animationFrame);
            animationFrame = requestAnimationFrame(updateHeight);
        }
    }

    // Kör updateHeight en gång vid start för att sätta initial position
    updateHeight();
    window.addEventListener('scroll', handleScroll);
}

/**
 * Line animation stack
 */
if (document.getElementById('line-stack-animation')) {
    // Get the container element with the ID 'line-stack-container'
    const stackContainer = document.getElementById('line-stack-container');
    
    // Get the wrapper element with the ID 'line-stack-wrapper'
    const stackWrapper = document.getElementById('line-stack-wrapper');
    
    // Get the line element with the ID 'line-stack-svg'
    const stackLine = document.getElementById('line-stack-svg');
    
    // Set the height of the line element to match the height of the container
    stackLine.style.height = '1950' + 'px';

    // Get the card1 element with the ID 'line-stack-card-1'
    const stackCard1 = document.getElementById('line-stack-card-1');
    
    // Calculate the vertical center position of card1
    const stackCard1_center = stackCard1.offsetTop + (stackCard1.offsetHeight / 2);

    // Get the card2 element with the ID 'line-stack-card-2'
    const stackCard2 = document.getElementById('line-stack-card-2');
    
    // Calculate the vertical center position of card2
    const stackCard2_center = stackCard2.offsetTop + (stackCard2.offsetHeight / 2);

    // Initialize a flag to track scrolling state
    let isScrolling = false;

    // Create a debounce function to limit the frequency of scroll event handling
    function debounce(func, wait = 100) {
        let timeout;
        return function (...args) {
            const later = () => {
                timeout = null;
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    // Function to update the height of the wrapper based on scroll position
    function updateHeight() {
        if (window.scrollY < stackCard1_center) {
            stackWrapper.style.maxHeight = '0rem';
        } else if (window.scrollY > stackCard1_center && window.scrollY < stackCard2_center) {
            stackWrapper.style.maxHeight = stackCard1_center + 'px';
        } else if (window.scrollY > stackCard2_center) {
            stackWrapper.style.maxHeight = stackCard2_center + 'px';
        }
        isScrolling = false;
    }

    // Function to handle scroll events and trigger the updateHeight function
    function handleScroll() {
        if (!isScrolling) {
            isScrolling = true;
            requestAnimationFrame(updateHeight);
        }
    }

    // Add a scroll event listener with debounce to improve performance
    window.addEventListener('scroll', debounce(handleScroll));
}

// Content is loaded
document.addEventListener("DOMContentLoaded", function () {

	// BalanceText - Balanserar textrader för bättre läsbarhet
	// Initialize BalanceText on elements with .balance-text class
	if (document.querySelector('.balance-text')) {
		balanceText('.balance-text', { watch: true });
	}

	// FAQ Accordion
	const faqButtons = document.querySelectorAll('[data-faq-toggle]');
	if (faqButtons.length > 0) {
		faqButtons.forEach(button => {
			// Handle click events
			button.addEventListener('click', function() {
				toggleFaqItem(this);
			});

			// Handle keyboard events (Enter and Space)
			button.addEventListener('keydown', function(e) {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					toggleFaqItem(this);
				}
			});
		});

		// Toggle function for FAQ items
		function toggleFaqItem(element) {
			const faqItem = element.closest('.faq-item');
			const content = faqItem.querySelector('[data-faq-content]');
			const icon = element.querySelector('.faq-icon');
			const isExpanded = element.getAttribute('aria-expanded') === 'true';

			// Close all other FAQ items
			faqButtons.forEach(otherButton => {
				if (otherButton !== element) {
					const otherItem = otherButton.closest('.faq-item');
					const otherContent = otherItem.querySelector('[data-faq-content]');
					const otherIcon = otherButton.querySelector('.faq-icon');

					otherButton.setAttribute('aria-expanded', 'false');
					otherContent.style.maxHeight = '0px';
					otherIcon.style.transform = 'rotate(0deg)';
				}
			});

			// Toggle current item
			if (isExpanded) {
				element.setAttribute('aria-expanded', 'false');
				content.style.maxHeight = '0px';
				icon.style.transform = 'rotate(0deg)';
			} else {
				element.setAttribute('aria-expanded', 'true');
				content.style.maxHeight = content.scrollHeight + 'px';
				icon.style.transform = 'rotate(180deg)';
			}
		}
	}

    // Load slider code only when the component is present.
    if (document.querySelector('.js-content-slider')) {
        loadComponentStyles().then(() => import(/* webpackChunkName: "content-slider" */ './components/content-slider'))
            .then(({ initContentSliders }) => initContentSliders())
            .catch(error => console.error('Could not load content sliders.', error));
    }

    // Video placeholder functionality
    // Kontrollera om det finns några video-placeholders på sidan för att undvika att köra onödig kod
    const placeholders = document.querySelectorAll(".video-placeholder");
    if (placeholders.length > 0) {
        placeholders.forEach(function (placeholder) {
            placeholder.addEventListener("click", function () {
                const videoSrc = placeholder.getAttribute("data-video-src");
                if (videoSrc) {
                    const videoPoster = placeholder.getAttribute("data-video-poster");
                    const videoAutoplay = placeholder.getAttribute("data-video-autoplay") === "1";

                    const videoWrapper = document.createElement("div");
                    videoWrapper.classList.add("relative", "w-full", "h-0", "pb-[56.25%]", "overflow-hidden", "rounded-cashit-xl", "bg-black");

                    const video = document.createElement("video");
                    video.classList.add("absolute", "inset-0", "w-full", "h-full", "object-cover");
                    video.setAttribute("controls", "controls");
                    video.setAttribute("playsinline", "playsinline");
                    video.setAttribute("preload", "metadata");
                    video.setAttribute("src", videoSrc);

                    if (videoPoster) {
                        video.setAttribute("poster", videoPoster);
                    }

                    if (videoAutoplay) {
                        video.setAttribute("autoplay", "autoplay");
                        video.setAttribute("muted", "muted");
                        video.setAttribute("loop", "loop");
                        video.muted = true;
                    }

                    videoWrapper.appendChild(video);
                    placeholder.replaceWith(videoWrapper);

                    video.play().catch(function () {});
                    return;
                }

                let embedCode = placeholder.getAttribute("data-embed");
                if (!embedCode) {
                    return;
                }

                // Lägg till autoplay om det är en YouTube eller Vimeo-länk
                if (embedCode.includes("youtube.com") || embedCode.includes("vimeo.com")) {
                    embedCode = embedCode.replace("?", "?autoplay=1&");
                }

                // Skapa en wrapper för att behålla aspect ratio
                const videoWrapper = document.createElement("div");
                videoWrapper.classList.add("relative", "w-full", "h-0", "pb-[56.25%]", "overflow-hidden");
                
                // Modifiera iframe-attributen för bättre responsivitet
                embedCode = embedCode.replace('<iframe', '<iframe style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;"');
                videoWrapper.innerHTML = embedCode;

                // Ersätt placeholder med videon
                placeholder.replaceWith(videoWrapper);
            });
        });
    }
});

// jQuery functions
jQuery(document).ready(function () {

    // Function to load content based on category
    function loadContentByCategory(category) {
        jQuery.ajax({
            type: 'GET',
            url: '/wp/wp-admin/admin-ajax.php',
            data: { 
                action: 'yabie_filter_partners_ajax', // Ajax action name
                category: category 
            },
            success: function (response) {
                jQuery('#partner-grid').html(response);
            },
            error: function (error) {
                console.error('Error:', error);
            }
        });
    }

    // Check URL on page load and load the content
    var urlParams = new URLSearchParams(window.location.search);
    var initialCategory = urlParams.get('category');
    if (initialCategory) {
        jQuery('#category-filter').val(initialCategory); // Set the dropdown value
        loadContentByCategory(initialCategory); // Load the content based on the category
    }

    // Filter partners by category on dropdown change
    jQuery('#category-filter').on('change', function () {
        var selectedCategory = jQuery(this).val();

        // Update the URL
        var newUrl = new URL(window.location.href);
        if (selectedCategory) {
            newUrl.searchParams.set('category', selectedCategory);
        } else {
            newUrl.searchParams.delete('category'); // Remove the category if empty
        }
        history.pushState(null, '', newUrl);

        // Load content based on the selected category
        loadContentByCategory(selectedCategory);
    });

    // Filter integrations by category (AJAX)
    jQuery('[data-integration-category-filter]').on('change', function () {
        const filter = jQuery(this);
        const selectedCategory = filter.val();
        const termIds = filter.data('term-ids');
        const items = filter
            .closest('[data-integration-gallery-row]')
            .find('[data-integration-items]')
            .first();

        jQuery.ajax({
            type: 'GET',
            url: '/wp/wp-admin/admin-ajax.php',
            data: {
                action: 'cashit_filter_integrations_ajax',
                category: selectedCategory,
                term_ids: termIds
            },
            success: function (response) {
                items.html(response);
            },
            error: function (error) {
                console.error('Error:', error);
            }
        });
    });

	// Wrap elements in classes if current page is .story-page
	if ( jQuery('.story-page').length ) {

		// Wrap .wp-video in .full-video div
		jQuery('.wp-video').wrap('<div class="full-video"></div>');
		
		// Wrap image that start with wp-image-* in .full-image div
		jQuery('.story-page .wp-caption').wrap('<div class="full-image"></div>');
		jQuery('.story-page img[class*="wp-image-"]').filter(function() {
			return !jQuery(this).closest('.wp-caption').length;
		}).wrap('<div class="full-image"></div>');

		// Wrap .news-gallery in .full-gallery div
		jQuery('.news-gallery').wrap('<div class="full-gallery"></div>');

		// Add class to each <p> or image preceding <h2>, <h3>, <h4>, or <h5> inside .story-text-styles
		jQuery('.story-text-styles h2, .story-text-styles h3, .story-text-styles h4, .story-text-styles h5').each(function() {
			var $heading = jQuery(this);
			var $prev = $heading.prev();

			// If previous element is an image or wp-caption, add spacer to it
			if ($prev.is('img, figure, .wp-caption')) {
				if (!$prev.hasClass('paragraph-spacer')) {
					$prev.addClass('paragraph-spacer');
				}
				return;
			}

			// Add class if it's a paragraph (and not already having the classes)
			if ($prev.is('p') && !$prev.hasClass('paragraph-spacer') && !$prev.hasClass('preamble')) {
				$prev.addClass('paragraph-spacer');
			}
		});

	}

	// Wrap elements in classes if current page is .news-page
	if ( jQuery('.news-page').length ) {

		// Add class to each <p> or image preceding <h2>, <h3>, <h4>, or <h5> inside .news-text-styles
		jQuery('.news-text-styles h2, .news-text-styles h3, .news-text-styles h4, .news-text-styles h5').each(function() {
			var $heading = jQuery(this);
			var $prev = $heading.prev();

			// If previous element is an image or wp-caption, add spacer to it
			if ($prev.is('img, figure, .wp-caption')) {
				if (!$prev.hasClass('paragraph-spacer')) {
					$prev.addClass('paragraph-spacer');
				}
				return;
			}

			// Add class if it's a paragraph (and not already having the classes)
			if ($prev.is('p') && !$prev.hasClass('paragraph-spacer') && !$prev.hasClass('preamble')) {
				$prev.addClass('paragraph-spacer');
			}
		});

	}

	// Hubspot form
	// Function to check input value and toggle class accordingly
	function updateLabelState(input) {
		let $label = jQuery(input).closest('.hs-form-field').find('label');
		let value = jQuery(input).val();
		if (value && value.trim() !== '') {
			$label.addClass('label-focused');
		} else {
			$label.removeClass('label-focused');
		}
	}

	function updateAllHubspotLabelStates() {
		jQuery('.hs-input').each(function() {
			updateLabelState(this);
		});
	}

	// Event delegation for dynamically loaded inputs
	jQuery(document).on('focus', '.hs-input', function() {
		jQuery(this).closest('.hs-form-field').find('label').addClass('label-focused');
	}).on('blur', '.hs-input', function() {
		updateLabelState(this);
	}).on('input change animationstart', '.hs-input', function() {
		updateLabelState(this);
	});

	// Check on page load for pre-filled fields (e.g., autofill)
	updateAllHubspotLabelStates();

	// Check again after a short delay to catch any autofilled values
	setTimeout(updateAllHubspotLabelStates, 100);
	setTimeout(updateAllHubspotLabelStates, 500);

	window.addEventListener('message', function(event) {
		if (event.data.type === 'hsFormCallback' && event.data.eventName === 'onFormReady') {
			setTimeout(updateAllHubspotLabelStates, 100);
			setTimeout(updateAllHubspotLabelStates, 500);
		}
	});

	// Move legal consent container into submit area for all forms
	function moveLegalConsentContainers() {
		const forms = document.querySelectorAll('.hbspt-form');
		
		forms.forEach(function(form) {
			const legalConsentContainer = form.querySelector('.legal-consent-container');
			const submitContainer = form.querySelector('.hs_submit .actions');
			
			if (legalConsentContainer && submitContainer && !legalConsentContainer.dataset.moved) {
				// Move the legal consent container to be a sibling of the submit button
				submitContainer.parentNode.insertBefore(legalConsentContainer, submitContainer);
				legalConsentContainer.dataset.moved = 'true';
			}
		});
	}

	// Listen for HubSpot form events
	window.addEventListener('message', function(event) {
		if (event.data.type === 'hsFormCallback' && event.data.eventName === 'onFormReady') {
			setTimeout(moveLegalConsentContainers, 100);
		}
	});

	// Fallback: Check periodically for the form elements
	let checkInterval = setInterval(function() {
		const forms = document.querySelectorAll('.hbspt-form');
		const unmovedContainers = document.querySelectorAll('.legal-consent-container:not([data-moved])');
		
		if (forms.length > 0 && unmovedContainers.length > 0) {
			moveLegalConsentContainers();
		}
		
		// Clear interval if all containers are moved
		if (unmovedContainers.length === 0) {
			clearInterval(checkInterval);
		}
	}, 500);

	// Clear interval after 15 seconds to avoid infinite checking
	setTimeout(function() {
		clearInterval(checkInterval);
	}, 15000);

	
	// Lazy load file upload functionality only when needed
	function checkForFileUploads() {
		const fileFields = document.querySelectorAll('.hs-fieldtype-file');
		if (fileFields.length > 0) {
			initFileUploads();
		}
	}

	// Check when HubSpot forms are ready
	window.addEventListener('message', function(event) {
		if (event.data.type === 'hsFormCallback' && event.data.eventName === 'onFormReady') {
			setTimeout(checkForFileUploads, 200);
		}
	});

	// Fallback check
	setTimeout(checkForFileUploads, 1000);

	// Check for experience cookie consent for HubSpot forms
	function checkExperienceCookieConsent() {
		// Get the cookie value
		const cookieValue = getCookie('cookies_and_content_security_policy');

		// Check if the cookie contains "experience"
		const hasExperienceConsent = window.CashitConsent
			? window.CashitConsent.has('experience')
			: cookieValue && cookieValue.includes('"experience"');

		// Get language from HTML lang attribute
		const lang = document.documentElement.lang;
		const isNorwegian = lang && lang.toLowerCase().startsWith('nb');

		// Define messages in both languages
		const messages = {
			sv: {
				title: 'Cookie-samtycke krävs',
				text1: 'För att kunna använda formuläret behöver vi ditt samtycke för cookies av typen "Upplevelse".',
				text2: 'Formuläret skyddas mot spam med hjälp av Google Recaptcha, som använder cookies och localStorage för att verifiera att du är en riktig person och inte en robot. Vi kan inte använda dessa utan ditt samtycke.',
				alternative: '<a href="#cookiesAndContentPolicySettings" class="underline font-semibold">Uppdatera dina cookie-inställningar</a>, eller kontakta oss direkt på <a href="mailto:info@cash-it.se" class="underline font-semibold">info@cash-it.se</a> om du föredrar det.'
			},
			nb: {
				title: 'Informasjonskapselsamtykke kreves',
				text1: 'For å kunne bruke skjemaet trenger vi ditt samtykke for informasjonskapsler av typen "Opplevelse".',
				text2: 'Skjemaet er beskyttet mot spam ved hjelp av Google Recaptcha, som bruker informasjonskapsler og localStorage for å bekrefte at du er en ekte person og ikke en robot. Vi kan ikke bruke disse uten ditt samtykke.',
				alternative: '<a href="#cookiesAndContentPolicySettings" class="underline font-semibold">Oppdater informasjonskapselinnstillingene dine</a>, eller kontakt oss direkte på <a href="mailto:info@cash-it.se" class="underline font-semibold">info@cash-it.se</a> hvis du foretrekker det.'
			}
		};

		// Select appropriate message based on language
		const message = isNorwegian ? messages.nb : messages.sv;

		// Find all HubSpot forms
		const forms = document.querySelectorAll('.hbspt-form');

		forms.forEach(function(form) {
			// Check if message already exists
			if (form.querySelector('.cookie-consent-message')) {
				return;
			}

			if (!hasExperienceConsent) {
				// Create and insert warning message
				const messageDiv = document.createElement('div');
				messageDiv.className = 'cookie-consent-message bg-cashit-yellow-100 p-[30px] rounded-[30px] mb-8';
				messageDiv.innerHTML = '<h3 class="text-base font-semibold mb-2">' + message.title + '</h3><p class="text-sm mb-2">' + message.text1 + '</p><p class="text-sm mb-2">' + message.text2 + '</p><p class="text-sm">' + message.alternative + '</p>';

				// Insert message at the top of the form
				form.insertBefore(messageDiv, form.firstChild);

				// Reinitialize cookie plugin's link handler for dynamically added links
				if (typeof openCookiesAndContentPolicySettingsLink === 'function') {
					openCookiesAndContentPolicySettingsLink();
				}
			}
		});
	}

	// Helper function to get cookie value
	function getCookie(name) {
		const value = `; ${document.cookie}`;
		const parts = value.split(`; ${name}=`);
		if (parts.length === 2) return decodeURIComponent(parts.pop().split(';').shift());
		return null;
	}

	// Listen for HubSpot form events
	window.addEventListener('message', function(event) {
		if (event.data.type === 'hsFormCallback' && event.data.eventName === 'onFormReady') {
			setTimeout(checkExperienceCookieConsent, 100);
		}
	});

	// Fallback: Check periodically for the forms
	let cookieCheckInterval = setInterval(function() {
		const forms = document.querySelectorAll('.hbspt-form');
		if (forms.length > 0) {
			checkExperienceCookieConsent();
		}
	}, 500);

	// Clear interval after 15 seconds
	setTimeout(function() {
		clearInterval(cookieCheckInterval);
	}, 15000);

});
